/**
 * What the dialog layer looks like, in one file.
 *
 * The same field was typed out in four places and the same button in three,
 * each a long Tailwind string that nobody would diff against the others. That
 * is how a border ends up slate-300 in one dialog and gray-300 in the next, and
 * why the toast looked like it came from a different site than the dialog it
 * replaced.
 *
 * Everything visual that more than one file needs lives here. A file that needs
 * something once still keeps it local: this is a vocabulary, not a stylesheet.
 */

/** The card every surface in this layer is made of. */
const SURFACE = 'rounded-2xl bg-white shadow-2xl ring-1 ring-slate-900/5 dark:bg-gray-800 dark:ring-white/10';

/** A text input, a textarea, a select. */
const FIELD = 'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 '
    + 'placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 '
    + 'dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-slate-500';

/** The label above one. */
const LABEL = 'mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300';

/** The quiet line under one. */
const HINT = 'mt-1 text-xs text-slate-500 dark:text-slate-400';

const BUTTON = 'inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold '
    + 'transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 '
    + 'disabled:cursor-not-allowed disabled:opacity-60 dark:focus-visible:ring-offset-gray-800';

/**
 * Three tones, and only three. An arbitrary colour is how a codebase ends up
 * with two different reds that mean the same thing.
 */
const TONES = {
    primary: 'bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-blue-500',
    danger: 'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500',
    neutral: 'bg-white text-slate-700 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 '
        + 'focus-visible:ring-slate-400 dark:bg-gray-800 dark:text-slate-200 dark:ring-gray-600 dark:hover:bg-gray-700'
};

/**
 * The chip that says what kind of message this is.
 *
 * The same five in the dialog and in the toast, so that a saved file looks like
 * a saved file whichever of the two it arrives in.
 */
const CHIPS = {
    success: { icon: 'fa-check', wrap: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400' },
    error: { icon: 'fa-xmark', wrap: 'bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-400' },
    warning: { icon: 'fa-triangle-exclamation', wrap: 'bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400' },
    info: { icon: 'fa-info', wrap: 'bg-blue-100 text-blue-600 dark:bg-blue-500/15 dark:text-blue-400' },
    question: { icon: 'fa-question', wrap: 'bg-slate-100 text-slate-500 dark:bg-gray-700 dark:text-slate-300' }
};

/**
 * @param {string} tag
 * @param {string} [className]
 * @param {string} [text]
 * @returns {HTMLElement}
 */
function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = String(text);
    return node;
}

/**
 * @param {string} [tone] - a key of CHIPS
 * @param {string} [size] - 'md' for a dialog, 'sm' for a toast
 * @returns {HTMLElement|null} null when the tone is not one we draw
 */
function chip(tone, size) {
    const found = CHIPS[tone];
    if (!found) return null;

    const box = size === 'sm' ? 'h-7 w-7 text-xs' : 'h-10 w-10';
    const wrap = el('div', 'flex shrink-0 items-center justify-center rounded-lg ' + box + ' ' + found.wrap);
    wrap.appendChild(el('i', 'fa-solid ' + found.icon));
    return wrap;
}

module.exports = { SURFACE, FIELD, LABEL, HINT, BUTTON, TONES, CHIPS, el, chip };
